import { useEffect, useRef } from 'react'
import lottie from 'lottie-web'
import DefaultLayout from 'components/layouts/default'
import styles from './index.module.scss'
import BreadCrumb from 'components/ui/breadcrumb'
import { isMobile } from 'utils/mobileDetect'
import { useRouter } from 'next/router'

const About = (props) => {
  const router = useRouter()
  const { asPath } = router

  const breadcrumb = [
    {
      text: 'Ana Sayfa',
      url: process.env.NEXT_PUBLIC_MAIN_URL2,
    },
    {
      text: 'Hakkımızda',
    },
  ]

  const breadcrumbHead = {
    '@context': 'http://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Ana Sayfa',
        item: process.env.NEXT_PUBLIC_MAIN_URL2,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Hakkımızda',
      },
    ],
  }

  const animationRef = useRef(null)
  const targetRefs = {
    [asPath]: useRef(null),
  }

  useEffect(() => {
    if (asPath && targetRefs[asPath]?.current) {
      targetRefs[asPath].current.scrollIntoView({
        behavior: 'smooth',
      })
    }
  }, [router.query])

  useEffect(() => {
    const animationInstance = lottie.loadAnimation({
      container: animationRef.current,
      renderer: 'svg',
      loop: false,
      autoplay: false,
      path: '/lotties/sailor-looking-through-telescope.json',
    })

    const handleScroll = () => {
      if (!animationInstance) return

      const scrollY = window.scrollY
      const maxScrollHeight = document.body.scrollHeight - window.innerHeight
      const maxFrames = animationInstance.totalFrames
      const frame = Math.round((scrollY / maxScrollHeight) * (maxFrames - 1))
      const clampedFrame = Math.min(Math.max(frame, 0), maxFrames - 1)

      animationInstance.goToAndStop(clampedFrame, true)
    }

    window.addEventListener('scroll', handleScroll)

    return () => {
      window.removeEventListener('scroll', handleScroll)
      animationInstance.destroy()
    }
  }, [])

  return (
    <DefaultLayout
      canonical={`${process.env.NEXT_PUBLIC_MAIN_URL2}/hakkimizda`}
      title="Hakkımızda"
      description="2017 yılında kurulan İşin Olsun, iş arayanlar ile işverenleri ve hizmet arayanlar ile hizmet verenleri buluşturan, hızlı ve etkili bir ilan platformu olarak destek sunmaya devam ediyor."
      headerType={2}
      {...props}
    >
      <div className={styles.root} data-test="page-about">
        <BreadCrumb
          breadcrumb={breadcrumb}
          breadcrumbHead={breadcrumbHead}
          className="v3"
          addHead
        />
        <div className="container">
          <h2>Hikayemiz</h2>
          <div
            data-test="section1"
            className={`${styles.section} ${styles.section1}`}
          >
            <div className={styles.left}>
              <div className={styles.img}>
                {!isMobile() && (
                  <div
                    className={styles.img}
                    ref={animationRef}
                    style={{
                      transform: 'scale(1.4)',
                    }}
                  />
                )}
              </div>
            </div>
            <div className={styles.right}>
              <ul>
                <li>
                  <p>
                    <b>2017</b> yılında arkamızda <b>Kariyer.net’in gücüyle,</b>{' '}
                    Türkiye’nin lokasyon bazlı ilk mavi yaka iş ve çalışan bulma
                    uygulaması olarak yolculuğumuza başladık. Bugün geldiğimiz
                    noktada mavi yaka iş ve çalışan arayışında{' '}
                    <b>Türkiye’nin en büyük ücretsiz platformu olduk.</b>
                  </p>
                </li>
                <li>
                  <p>
                    Geleneksel yöntemlerin ihtiyaçlara cevap verme noktasında
                    yetersiz kaldığı mavi yaka istihdamına dijital çözüm olduk.
                    İşverenlerin aradığı çalışanı, adayların da istediği işi
                    kolayca bulmasını sağlamak amacıyla kurulduk.
                  </p>
                </li>
                <li>
                  <p>
                    Ücretsiz mavi yaka iş ve çalışan bulma uygulaması olarak
                    bugüne kadar <b>10 milyonu aşkın indirme sayısına</b>{' '}
                    ulaştık. <b>6 milyondan fazla adayın</b> kullandığı
                    uygulamamız üzerinden bugüne kadar{' '}
                    <b>141 milyon iş başvurusu</b> gerçekleşti.
                  </p>
                </li>
                <li>
                  <p>
                    <b>1 milyonu aşkın işverene</b> ev sahipliği yapan
                    uygulamamız ile{' '}
                    <b>2,.5 milyondan fazla kişinin iş bulmasına</b> aracı
                    olduk.
                  </p>
                </li>
                <li>
                  <p>
                    Adaylar ile işverenleri bir araya getirirken
                    kullanıcılarımızın güvenliğini ve memnuniyetini her zaman
                    önceliklerimizin ilk sıralarında konumlandırdık.{' '}
                    <b>Numara Maskeleme</b> özelliğimiz ile iş arayan ve
                    işverenlerin iş görüşmelerini kişisel numara paylaşımı
                    olmadan güvenle yapabilmesini sağladık.
                  </p>
                </li>
                <li>
                  <p>
                    Hizmet vermeye başladığımız günden bu yana; alanında uzman
                    ilan kontrol ekibimiz ile ilanlarımızı düzenli ve sürekli
                    olarak <b>kontrol ediyoruz.</b>
                  </p>
                </li>
                <li>
                  <p>
                    Bir ilan yayınlanırken içeriğindeki kelimeleri belirli
                    teknolojiler yardımıyla tespit edilerek kara ve gri
                    dediğimiz listelerle karşılaştırıyoruz. Buna göre de bazı
                    ilanları yayına almıyoruz. Yanıltıcı ilanları kaldırıyoruz.
                    Adaylarımızın şikayetlerine de destek ekibimizle yardımcı
                    oluyoruz.
                  </p>
                </li>
                <li>
                  <p>
                    Dijital çözüm olarak çıktığımız yolda ihtiyaçları analiz
                    etmeye ve çözüm geliştirmeye devam ettik. Hem işverenlerin
                    hem adayların lokasyon bazlı aramalarını daha kolay
                    yapabilmesi adına uygulamamızı yayına aldıktan kısa bir süre
                    sonra <b>Harita Görünümü</b> özelliğimizi hayata geçirdik.
                  </p>
                </li>
                <li>
                  <p>
                    <b>Akıllı Filtreleme</b> gibi özelliklerimiz ile adayların
                    daha daha kısa sürede aradığı ilanlara ulaşmasını
                    kolaylaştıran çözümler geliştirirken işverenlerin acil
                    çalışan ihtiyacını hızla karşılayabilmesi için{' '}
                    <b>Acil İlan</b> gibi özelliklerimizi uygulamamıza ekledik.
                  </p>
                </li>
                <li>
                  <p>
                    Mavi yaka iş ve çalışan ihtiyacının yanı sıra hizmet almak
                    veya vermek isteyenleri de düşünerek <b>Hizmet</b> ürün
                    geliştirmesiyle iş/çalışan ihtiyacını uçtan uca karşılamaya
                    başladık.
                  </p>
                </li>
                <li>
                  <p>
                    Tam zamanlı ve part-time iş ilanlarına ek olarak{' '}
                    <b>&quot;Günlük/Haftalık Hizmet&quot;</b> iş ilanlarının da
                    İşin Olsun&apos;da yer alabilmesini sağladık. Ev
                    temizliğinden nakliyata, tamirattan boya-badanaya kadar pek
                    çok kısa süreli veya tek seferlik işler için bu ürünü
                    geliştirdik.
                  </p>
                </li>
                <li>
                  <p>
                    Hem adayların hem de işverenlerin değişen ihtiyaçlarına
                    sürdürülebilir ve kalıcı çözümler üretmek için çalışmaya
                    devam edeceğiz.
                  </p>
                </li>
              </ul>
            </div>
          </div>
        </div>
        <div className="container">
          <h2>Neler Sunuyoruz</h2>
          <div
            data-test="section2"
            className={`${styles.section} ${styles.section2}`}
          >
            <div className={styles.left}>
              <div ref={targetRefs['/hakkimizda/isverenlere-ozel']}>
                <h3>İşverenlere Özel</h3>
                <div className={styles.group}>
                  <h4>Grup Yönetim Modülü</h4>
                  <p>
                    Geniş satış ağınızı kolayca yönetebilirsiniz; bayilerinizin
                    hızlı ve kolayca ilan yayınlamasını sağlayabilirsiniz, mavi
                    yakada doğru hedef kitleye kolayca ulaşabilirsiniz.
                  </p>
                </div>
                <div className={styles.group}>
                  <h4>İlan Paketi</h4>
                  <p>
                    İlan paketi sayesinde İşin Olsun ile birlikte Kariyer.net’te
                    de ilanınızı yayınlayabilir ve Kariyer.net 360 üzerinden tek
                    panelden ilanlarınızı yönetebilirsiniz.
                  </p>
                </div>
                <div className={styles.group}>
                  <h4>İletişim Paketleri</h4>
                  <p>
                    İş ilanlarınızı İşin Olsun’da hızlıca doğru kitleye
                    ulaştırarak açık pozisyonların hızlıca kapanmasını
                    sağlayabilirsiniz.
                  </p>
                </div>
              </div>
              <div ref={targetRefs['/hakkimizda/is-arayanlara-ozel']}>
                <h3>İş Arayanlara Özel</h3>
                <div className={styles.group}>
                  <p>
                    Mobil uygulamamız aracılığıyla iş arayan adaylar; saniyeler
                    içinde profilini oluşturup, iş fırsatlarını keşfedebilir, iş
                    başvurusu yapma ve işverenler tarafından keşfedilme imkânına
                    sahip olur. Lokasyon bazlı algoritmamız ve harita
                    özelliğimiz sayesinde adaylar kendilerine yakın yerlerdeki
                    iş fırsatlarını kolayca görebilir, diledikleri ilanlara
                    başvurabilir.
                  </p>
                  <p>
                    Hızlıca üye olarak, adaylarımız iş aramanın ilk adımını
                    kolayca atabiliyor. Uygulamamızda kolayca profilini
                    oluşturuyor ve yüz binlerce ilan arasından kendine uygun
                    olanlara başvurabiliyor. Türkiye’nin en fazla iş ilanı
                    yayınlanan iş bulma platformu İşin Olsun olarak, mavi yaka
                    adayların hızlıca iş bulmasına aracılık ediyoruz.
                  </p>
                </div>
              </div>
              <div>
                <h3>Hizmet</h3>
                <div className={styles.group}>
                  <p>
                    <b>
                      2022 yılında hayata geçirdiğimiz Hizmet ürün geliştirmesi
                      ile iş / çalışan ihtiyaçlarını uçtan uca karşılıyoruz.
                    </b>
                  </p>
                  <p>
                    Tam zamanlı ve part-time iş ilanlarına ek olarak
                    &quot;Günlük/Haftalık Hizmet&quot; iş ilanları İşin Olsun’da
                    yer alıyor. Ev temizliğinden nakliyata, tamirattan
                    boya-badanaya kadar pek çok kısa süreli veya tek seferlik
                    işler için
                    <br />
                    günlük / haftalık hizmet ilanları verilebiliyor veya hizmet
                    alınabiliyor. Hizmet alanlar kısa süreli veya tek seferlik
                    işlerini yapabilecek çalışanlar bulabilirken hizmet verenler
                    de hızlı, kolayca iş bulabiliyor ve ek gelir elde
                    edebiliyor.
                  </p>
                </div>
              </div>
            </div>
            <div className={styles.right}>
              <img
                alt="neler sunuyoruz"
                loading="lazy"
                src={process.env.NEXT_PUBLIC_MAIN_URL2 + '/images/about-2.svg'}
              />
            </div>
          </div>
        </div>
      </div>
    </DefaultLayout>
  )
}
export default About
